import React, { useState, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from '@/components/Layout/Sidebar';
import Header  from '@/components/Layout/Header';
import MobileNav from '@/components/Layout/MobileNav';
import EmailVerificationBanner from '@/components/ui/EmailVerificationBanner';
import LoadingScreen from '@/components/ui/LoadingScreen';
import { usePageLoader } from '@/contexts/PageLoaderContext';

interface AppLayoutProps {
  children?: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isLoading, message } = usePageLoader();

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside id="sidebar" className="hidden lg:flex flex-col flex-shrink-0 w-64">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-50 h-full animate-slide-right">
            <Sidebar mobile onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <EmailVerificationBanner />

        {/* Top Progress Bar for instant feedback on route switch / data loading */}
        {isLoading && (
          <div className="h-1 w-full bg-primary-100 overflow-hidden flex-shrink-0 relative z-20">
            <div className="h-full bg-primary-600 animate-indeterminate rounded-r-full shadow-sm" />
          </div>
        )}

        <main className="flex-1 overflow-y-auto relative flex flex-col">
          {isLoading && (
            <div className="absolute inset-0 z-30 bg-gray-50/95 backdrop-blur-2xs flex items-center justify-center animate-fade-in">
              <LoadingScreen fullScreen={false} message={message} />
            </div>
          )}

          <div
            className={`flex-1 p-4 lg:p-6 pb-20 lg:pb-6 transition-opacity duration-200 ${
              isLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
          >
            <Suspense fallback={<LoadingScreen fullScreen={false} message={message || 'Loading page…'} />}>
              {children || <Outlet />}
            </Suspense>
          </div>
        </main>

        {/* Mobile bottom nav */}
        <MobileNav />
      </div>
    </div>
  );
};

export default AppLayout;
